import { Text } from '@lmc/ui-kit/src/general'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import TeamMembersPopover from 'screens/Rostering/Reconciliation/ChargeRates/popovers/TeamMembersPopover'
import { formatMinutes } from 'screens/Rostering/Reconciliation/reconciliationUtils'
import CanceledIcon from 'screens/Rostering/RotaBuilder/common/components/icons/CanceledIcon'
import DoneIcon from 'screens/Rostering/RotaBuilder/common/components/icons/DoneIcon'
import InProgressIcon from 'screens/Rostering/RotaBuilder/common/components/icons/InProgressIcon'
import OverdueIcon from 'screens/Rostering/RotaBuilder/common/components/icons/OverdueIcon'
import {
  getDateTimeInZone,
  TIME_FORMAT
} from 'screens/Rostering/utils.datetime'
import styled from 'styled-components'
import {
  payReportTableComponents,
  StyledPayReportTable
} from '../PayReport/PayReportTableComponents'
import type {
  ChargeEventStatus,
  ChargeRateType
} from '../types/chargeReconciliation'
import type { ChargeReportVisitRow } from '../types/chargeReport'

const EVENT_STATUS_ICON: Record<ChargeEventStatus, FC> = {
  DONE: DoneIcon,
  MISSED: OverdueIcon,
  CANCELLED: CanceledIcon,
  ONGOING: InProgressIcon,
  // Overdue reuses the missed icon until a dedicated design exists.
  OVERDUE: OverdueIcon
}

const EVENT_STATUS_LABEL_KEY: Record<ChargeEventStatus, string> = {
  DONE: 'RECONCILIATION_EVENT_STATUS_DONE',
  MISSED: 'RECONCILIATION_EVENT_STATUS_MISSED',
  CANCELLED: 'RECONCILIATION_EVENT_STATUS_CANCELLED',
  ONGOING: 'RECONCILIATION_EVENT_STATUS_ONGOING',
  OVERDUE: 'RECONCILIATION_EVENT_STATUS_OVERDUE'
}

type Translate = (key: string, options?: Record<string, unknown>) => string

const rateUnitKey = (rateType: ChargeRateType | null): string =>
  rateType === 'HOURLY' ? 'RATE_TYPE_HOURLY' : 'RATE_TYPE_FIXED'

function getColumns(t: Translate): ColumnsType<ChargeReportVisitRow> {
  return [
    {
      title: <HeaderCell>{t('RECONCILIATION_TABLE_DATE')}</HeaderCell>,
      key: 'date',
      width: 130,
      render: (_, record) => (
        <TimeStack>
          <CellPrimaryText>
            {dayjs(record.date).format('DD-MM-YYYY')}
          </CellPrimaryText>
          <CellSecondaryText>
            {getDateTimeInZone(record.plannedTime.startTime).format(
              TIME_FORMAT
            )}
            –{getDateTimeInZone(record.plannedTime.endTime).format(TIME_FORMAT)}
          </CellSecondaryText>
        </TimeStack>
      )
    },
    {
      title: (
        <HeaderCell>{t('CHARGE_REPORT_DRAWER_COL_ASSIGNED_TMS')}</HeaderCell>
      ),
      key: 'teamMembers',
      width: 100,
      render: (_, record) => (
        <TeamMembersPopover members={record.teamMembers}>
          <CountTrigger>
            <CellPrimaryText>
              {t('CHARGE_REPORT_DRAWER_TEAM_MEMBER_COUNT', {
                count: record.teamMembers.length
              })}
            </CellPrimaryText>
          </CountTrigger>
        </TeamMembersPopover>
      )
    },
    {
      title: <HeaderCell>{t('RECONCILIATION_TABLE_TYPE_OF_EVENT')}</HeaderCell>,
      key: 'eventType',
      width: 130,
      render: (_, record) => (
        <CellPrimaryText>{record.eventType.name}</CellPrimaryText>
      )
    },
    {
      title: (
        <HeaderCell>{t('RECONCILIATION_CHARGE_TABLE_EVENT_STATUS')}</HeaderCell>
      ),
      key: 'eventStatus',
      width: 120,
      render: (_, record) => {
        const StatusIcon = EVENT_STATUS_ICON[record.eventStatus]
        return (
          <EventStatusCell>
            <StatusIcon />
            <CellPrimaryText>
              {t(EVENT_STATUS_LABEL_KEY[record.eventStatus])}
            </CellPrimaryText>
          </EventStatusCell>
        )
      }
    },
    {
      title: (
        <HeaderCell>{t('RECONCILIATION_CHARGE_TABLE_CHARGED_TIME')}</HeaderCell>
      ),
      key: 'chargedTime',
      width: 130,
      render: (_, record) => {
        if (record.chargedTimeMode === 'not_charged') {
          return (
            <DangerText>
              <Text>{t('RECONCILIATION_NOT_CHARGED')}</Text>
            </DangerText>
          )
        }
        if (record.chargedTimeMode === 'custom') {
          return (
            <CellPrimaryText>
              {t('CHARGE_REPORT_CHARGED_CUSTOM')}
            </CellPrimaryText>
          )
        }
        if (!record.chargedTime) {
          return <CellPrimaryText>-</CellPrimaryText>
        }
        return (
          <TimeStack>
            <CellSecondaryText>
              {t('CHARGE_REPORT_CHARGED_PLANNED')}
            </CellSecondaryText>
            <CellPrimaryText>
              {getDateTimeInZone(record.chargedTime.startTime).format(
                TIME_FORMAT
              )}
              –
              {getDateTimeInZone(record.chargedTime.endTime).format(
                TIME_FORMAT
              )}
            </CellPrimaryText>
            <CellSecondaryText>
              {formatMinutes(record.chargedTime.durationMinutes)}
            </CellSecondaryText>
          </TimeStack>
        )
      }
    },
    {
      title: (
        <HeaderCell>{t('RECONCILIATION_CHARGE_TABLE_CHARGE_RATE')}</HeaderCell>
      ),
      key: 'chargeRate',
      width: 120,
      render: (_, record) => {
        if (record.chargeRate == null) {
          return <CellPrimaryText>-</CellPrimaryText>
        }
        return (
          <TimeStack>
            <CellPrimaryText>{record.chargeRate}</CellPrimaryText>
            <CellSecondaryText>
              {t(rateUnitKey(record.chargeRateType))}
            </CellSecondaryText>
          </TimeStack>
        )
      }
    },
    {
      title: (
        <HeaderCell>
          {t('RECONCILIATION_CHARGE_TABLE_CHARGE_AMOUNT')}
        </HeaderCell>
      ),
      key: 'chargeAmount',
      width: 120,
      render: (_, record) => {
        if (record.chargeAmount == null) {
          return <CellPrimaryText>-</CellPrimaryText>
        }
        return (
          <CellPrimaryText weight="bold">
            {record.chargeAmount.toFixed(2)} {t('RECONCILIATION_GBP_SUFFIX')}
          </CellPrimaryText>
        )
      }
    }
  ]
}

type ChargeReportBreakdownTableProps = {
  rows: ChargeReportVisitRow[]
  loading: boolean
}

export const ChargeReportBreakdownTable = ({
  rows,
  loading
}: ChargeReportBreakdownTableProps) => {
  const { t } = useTranslation()
  return (
    <HeaderHeightWrap>
      <StyledPayReportTable
        components={payReportTableComponents}
        columns={getColumns(t)}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="small"
        rowClassName={(_, index) => (index % 2 === 0 ? 'even-row' : 'odd-row')}
      />
    </HeaderHeightWrap>
  )
}

const HeaderHeightWrap = styled.div`
  & .ant-table-thead > tr > th {
    padding-top: 18px !important;
    padding-bottom: 18px !important;
  }
`

const HeaderCell = styled.span`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 500;
`

const CellPrimaryText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CellSecondaryText = styled(Text).attrs({ level: 2, weight: 'medium' })`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const TimeStack = styled.div`
  display: flex;
  flex-direction: column;
`

const EventStatusCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CountTrigger = styled.span`
  cursor: pointer;
`

const DangerText = styled.span`
  color: #f5222d;
`
